// ─────────────────────────────────────────────────────────────────────────────
//  platform/response.js — Uniform response envelope (Amendment A8)
//
//  EVERY command and query returns this exact shape:
//      { success, code, message, data, warnings, metadata }
//  Never bare, ad-hoc JSON. The frontend can rely on the shape unconditionally.
// ─────────────────────────────────────────────────────────────────────────────
import { AppError, ERRORS } from './errors';

// Success envelope. `code` is 'OK' on success; error codes come from the catalogue.
export function ok(data = null, { warnings = [], metadata = {} } = {}) {
  return {
    success: true,
    code: 'OK',
    message: 'OK',
    data,
    warnings: Array.isArray(warnings) ? warnings : [],
    metadata: metadata && typeof metadata === 'object' ? metadata : {},
  };
}

// Failure envelope. Accepts an AppError, a catalogue code string, or falls back
// to ERR_INTERNAL. Never leaks stack traces or raw messages to the client.
export function fail(error, { metadata = {} } = {}) {
  const base = { success: false, data: null, warnings: [] };

  if (error instanceof AppError) {
    return {
      ...base,
      code: error.code,
      message: error.message,
      metadata: { ...metadata, ...(error.details ? { details: error.details } : {}) },
    };
  }

  if (typeof error === 'string' && ERRORS[error]) {
    const s = ERRORS[error];
    return { ...base, code: s.code, message: s.message, metadata };
  }

  const s = ERRORS.ERR_INTERNAL;
  return { ...base, code: s.code, message: s.message, metadata };
}

// Guard used by the frontend/integration layer to validate a response shape.
export function isEnvelope(x) {
  return !!x
    && typeof x === 'object'
    && typeof x.success === 'boolean'
    && typeof x.code === 'string'
    && 'data' in x
    && Array.isArray(x.warnings)
    && x.metadata && typeof x.metadata === 'object';
}
